<template>
	<v-card class="solid-mesh-panel" color="panel_sub">
		<v-card-text class="pa-2">
			<v-row dense class="details-wrapper">
				<v-col cols="12">
					<v-card class="solid-mesh-detail-inner" :class="{ 'solid-mesh-detail-view': !isEditing }" color="panel_sub">
						<v-card-text class="pa-2">
							<v-row dense>
								<v-col cols="12">
									<div class="text-caption text-grey">{{ panelTitle }}</div>
								</v-col>
							</v-row>

							<!-- Dimensions -->
							<v-row dense>
								<v-col cols="12">
									<div class="text-caption">{{ dimensionsLabel }}</div>
									<template v-if="isEditing">
										<div class="field-stack mt-2">
											<DistanceSettingInput :model-value="rawInputs.dimensionX" :label="dimensionXLabel" active
												:base-unit="typeConfig.x.baseUnit" :step="dimensionXStep" :max-value="dimensionXMax"
												:min-value="dimensionXStep" :decimal-length="DIMENSION_DECIMALS" density="compact" hide-details
												variant="outlined" class="mb-1" @focus="onDimensionInputFocus" @blur="onDimensionInputBlur"
												@update:model-value="onDimensionXChange" />
											<!-- box-culvert shows Y dimension; manhole hides it -->
											<DistanceSettingInput v-if="structureType === SIMPLE_STRUCTURE_TYPE.BOX_CULVERT"
												:model-value="rawInputs.dimensionY" :label="dimensionYLabel" active
												:base-unit="typeConfig.y.baseUnit" :step="dimensionYStep" :max-value="dimensionYMax"
												:min-value="dimensionYStep" :decimal-length="DIMENSION_DECIMALS" density="compact" hide-details
												variant="outlined" class="mb-1" @focus="onDimensionInputFocus" @blur="onDimensionInputBlur"
												@update:model-value="onDimensionYChange" />
											<DistanceSettingInput :model-value="rawInputs.dimensionZ" :label="dimensionZLabel" active
												:base-unit="typeConfig.z.baseUnit" :step="dimensionZStep" :max-value="dimensionZMax"
												:min-value="dimensionZStep" :decimal-length="DIMENSION_DECIMALS" density="compact" hide-details
												variant="outlined" @focus="onDimensionInputFocus" @blur="onDimensionInputBlur"
												@update:model-value="onDimensionZChange" />
										</div>
									</template>
									<template v-else>
										<div class="text-body-2 mt-1">
											<div>{{ dimensionXLabel }}: {{ formattedDimensionX }}</div>
											<div v-if="structureType === 'box-culvert'">
												{{ dimensionYLabel }}: {{ formattedDimensionY }}
											</div>
											<div>{{ dimensionZLabel }}: {{ formattedDimensionZ }}</div>
										</div>
									</template>
								</v-col>
							</v-row>

							<!-- Anchor point header + display (non-editing) -->
							<v-row dense>
								<v-col cols="12" class="d-flex align-center justify-space-between">
									<div class="text-caption">{{ $t("ANCHOR_POINT") }}</div>
								</v-col>
								<template v-if="!isEditing">
									<v-col cols="12">
										<div class="text-body-2">
											X: {{ formatCoordinate(localAnchorPoint.x) }}, Y:
											{{ formatCoordinate(localAnchorPoint.y) }}, Z:
											{{ formatCoordinate(localAnchorPoint.z) }}
										</div>
									</v-col>
								</template>
							</v-row>

							<!-- Anchor X/Y/Z editing -->
							<template v-if="isEditing">
								<v-row dense>
									<v-col cols="12">
										<DistanceSettingInput :model-value="rawInputs.anchorX" label="X" :step="anchorPointStep" active
											:decimal-length="anchorPointDecimals" density="compact" hide-details variant="outlined"
											class="mb-1" :class="{ 'dragging-locked': isDraggingSolidMesh }"
											@update:model-value="onAnchorXChange" />
									</v-col>
									<v-col cols="12">
										<DistanceSettingInput :model-value="rawInputs.anchorY" label="Y" :step="anchorPointStep" active
											:decimal-length="anchorPointDecimals" density="compact" hide-details variant="outlined"
											class="mb-1" :class="{ 'dragging-locked': isDraggingSolidMesh }"
											@update:model-value="onAnchorYChange" />
									</v-col>
									<v-col cols="12">
										<DistanceSettingInput :model-value="rawInputs.anchorZ" label="Z" :step="anchorPointStep" active
											:decimal-length="anchorPointDecimals" density="compact" hide-details variant="outlined"
											:class="{ 'dragging-locked': isDraggingSolidMesh }" @update:model-value="onAnchorZChange" />
									</v-col>
								</v-row>
							</template>

							<!-- Transparency & Color -->
							<template v-if="isEditing">
								<v-row dense>
									<v-col cols="12">
										<div class="text-caption">{{ $t("TRANSPARENCY") }}</div>
										<v-text-field :model-value="rawInputs.transparency" type="number" :min="transparencyConstraint.min"
											:max="transparencyConstraint.max" :step="transparencyConstraint.step" density="compact"
											hide-details variant="outlined" suffix="%"
											@update:model-value="(v) => onTransparencyChangeLocal(v)" @input="onTransparencyInput" />
									</v-col>
								</v-row>
								<v-row dense>
									<v-col cols="12">
										<div class="text-caption">{{ $t("COLOR") }}</div>
										<div class="d-flex align-center gap-2">
											<v-menu :close-on-content-click="false" location="bottom start">
												<template v-slot:activator="{ props: menuProps }">
													<div class="color-preview mr-2 cursor-pointer" :style="{ backgroundColor: editColorString }"
														v-bind="menuProps" />
												</template>
												<v-card min-width="300">
													<v-color-picker v-model="pickerColor" mode="rgb" hide-inputs show-swatches
														@update:model-value="onPickerColorChange" />
												</v-card>
											</v-menu>
											<v-text-field :model-value="rawInputs.colorR" label="R" type="number" :min="colorConstraint.min"
												:max="colorConstraint.max" :step="colorConstraint.step" density="compact" hide-details
												variant="outlined" style="max-width: 70px" persistent-placeholder placeholder=""
												@update:model-value="(v) => onColorRChange(v)" @input="onColorRInput" />
											<v-text-field :model-value="rawInputs.colorG" label="G" type="number" :min="colorConstraint.min"
												:max="colorConstraint.max" :step="colorConstraint.step" density="compact" hide-details
												variant="outlined" style="max-width: 70px" persistent-placeholder placeholder=""
												@update:model-value="(v) => onColorGChange(v)" @input="onColorGInput" />
											<v-text-field :model-value="rawInputs.colorB" label="B" type="number" :min="colorConstraint.min"
												:max="colorConstraint.max" :step="colorConstraint.step" density="compact" hide-details
												variant="outlined" style="max-width: 70px" persistent-placeholder placeholder=""
												@update:model-value="(v) => onColorBChange(v)" @input="onColorBInput" />
										</div>
									</v-col>
								</v-row>
							</template>
							<template v-else>
								<v-row dense>
									<v-col cols="3">
										<div class="text-caption my-2">{{ $t("TRANSPARENCY") }}</div>
										<div class="text-body-2">
											{{ Math.round(shapeColor.alpha * 100) }}%
										</div>
									</v-col>
									<v-col cols="9">
										<div class="text-caption my-2">{{ $t("COLOR") }}</div>
										<div class="d-flex align-center">
											<div class="color-preview mr-2" :style="{ backgroundColor: viewColorString }" />
											<div class="text-body-2">
												R: {{ viewColorR }}, G: {{ viewColorG }}, B: {{ viewColorB }}
											</div>
										</div>
									</v-col>
								</v-row>
							</template>
						</v-card-text>
					</v-card>
				</v-col>
			</v-row>
			<!-- Transform panel (box-culvert only; manhole excluded) -->
			<template v-if="isEditing && structureType === 'box-culvert'">
				<v-row dense>
					<v-col cols="12">
						<solid-mesh-transform-panel :solidMeshId="solidMeshId" :isEditing="isEditing" />
					</v-col>
				</v-row>
			</template>
		</v-card-text>
	</v-card>
</template>

<script setup lang="ts">
import { SIMPLE_STRUCTURE_TYPE } from "@/models/objects/solidMesh/types";
import { computed, onBeforeUnmount, reactive, ref, toRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import DistanceSettingInput from "../DistanceSettingInput.vue";
import SolidMeshTransformPanel from "./SolidMeshTransformPanel.vue";
import type { SolidMeshAxisKey } from "./composables/solidMeshUnit.types";
import {
	DIMENSION_DECIMALS,
	formatDimensionValue,
} from "./composables/useSolidMeshDimensionConfig";
import type { TSolidMeshCommonRawInputs } from "./composables/useSolidMeshEdit";
import {
	SIMPLE_STRUCTURE_EDIT_CONSTRAINTS,
	SIMPLE_STRUCTURE_DIMENSION_MAX,
	SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE,
	normalizeSpinnerInput,
	toOptionalNumber,
	useDimensionValidation,
	useInputActivity,
	useSolidMeshEdit,
} from "./composables/useSolidMeshEdit";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
const props = defineProps<{
	solidMeshId: string;
	isEditing: boolean;
}>();

// ---------------------------------------------------------------------------
// Setup
// ---------------------------------------------------------------------------
const store = useStore();
const { t } = useI18n();

const common = useSolidMeshEdit(
	toRef(props, "solidMeshId"),
	toRef(props, "isEditing"),
	store,
	t,
);

const {
	editData,
	localAnchorPoint,
	isUpdatingPosition,
	anchorFetchTimeout,
	solidMesh,
	structureType,
	panelTitle,
	typeConfig,
	convertLength,
	formatCoordinate,
	dimensionXLabel,
	dimensionYLabel,
	dimensionZLabel,
	dimensionXStep,
	dimensionYStep,
	dimensionZStep,
	scaledDimensions,
	formattedDimensionX,
	formattedDimensionY,
	editColorString,
	pickerColor,
	isDraggingSolidMesh,
	displayLocalZ,
	onTransparencyChange,
	onPickerColorChange: baseOnPickerColorChange,
	onColorChange,
	updateLocalAnchorPointX,
	updateLocalAnchorPointY,
	updateLocalAnchorPointZ,
	fetchLocalAnchorPoint,
	loadCommonEditData,
	syncDimensionsFromViewer,
	getColorString,
	initCommonRawInputs,
} = common;

import type { PlainCartesian3 as TPoint3 } from "earthbrain-viewer";

const rawInputs = reactive<TSolidMeshCommonRawInputs>({
	transparency: undefined,
	dimensionX: undefined,
	dimensionY: undefined,
	dimensionZ: undefined,
	colorR: undefined,
	colorG: undefined,
	colorB: undefined,
	anchorX: undefined,
	anchorY: undefined,
	anchorZ: undefined,
});

const {
	isDimensionInputting,
	isDimensionFieldFocused,
	dimensionInputTypingTimeout,
	onDimensionInputFocus,
	onDimensionInputBlur,
} = useInputActivity(500);

useDimensionValidation({
	rawInputs,
	isEditing: toRef(props, "isEditing"),
	structureType: toRef(common, "structureType"),
	store,
});

const isUpdatingDimensions = ref(false);
const dimensionsLabel = computed(() => t("DIMENSIONS"));
const dimensionXMax = SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE;
const dimensionYMax = SIMPLE_STRUCTURE_DIMENSION_MAX;
const anchorPointStep = SIMPLE_STRUCTURE_EDIT_CONSTRAINTS.anchorPoint.step;
const anchorPointDecimals =
	SIMPLE_STRUCTURE_EDIT_CONSTRAINTS.anchorPoint.decimalLength;
const transparencyConstraint = SIMPLE_STRUCTURE_EDIT_CONSTRAINTS.transparency;
const colorConstraint = SIMPLE_STRUCTURE_EDIT_CONSTRAINTS.colorChannel;
const dimensionZMax =
	structureType.value === "box-culvert"
		? SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE
		: SIMPLE_STRUCTURE_DIMENSION_MAX;

const formattedDimensionZ = computed(() => {
	const cfg = typeConfig.value;
	const val = Number(scaledDimensions.value.z);
	return formatDimensionValue(val, cfg.z, convertLength);
});

// View-mode color
const shapeColor = computed(() => {
	const sm = solidMesh.value as Record<string, unknown> | undefined;
	const config = sm?.shapeConfig as
		| { color: { red: number; green: number; blue: number; alpha: number } }
		| undefined;
	return config?.color ?? { red: 0, green: 0, blue: 0, alpha: 0 };
});
const viewColorString = computed(() => getColorString(shapeColor.value));
const viewColorR = computed(() => Math.round(shapeColor.value.red * 255));
const viewColorG = computed(() => Math.round(shapeColor.value.green * 255));
const viewColorB = computed(() => Math.round(shapeColor.value.blue * 255));

// ---------------------------------------------------------------------------
// Methods
// ---------------------------------------------------------------------------
function getSolidMesh() {
	return solidMesh.value as Record<string, unknown>;
}

// --- rawInputs change handlers ---
// DistanceSettingInput emits values in the axis base unit. Its clamp and decimal
// limit are applied to the displayed value, so on a feet site the same numeric
// limits are read as feet by design. These handlers only store and propagate.
function onDimensionXChange(val: number | undefined | null) {
	rawInputs.dimensionX = val ?? undefined;
	if (val === undefined || val === null) return;
	editData.dimensions.x = val;
	onDimensionsChange("x");
}

function onDimensionYChange(val: number | undefined | null) {
	rawInputs.dimensionY = val ?? undefined;
	if (val === undefined || val === null) return;
	editData.dimensions.y = val;
	onDimensionsChange("y");
}

function onDimensionZChange(val: number | undefined | null) {
	rawInputs.dimensionZ = val ?? undefined;
	if (val === undefined || val === null) return;
	editData.dimensions.z = val;
	onDimensionsChange("z");
}

function onTransparencyChangeLocal(val: unknown) {
	const normalized = toOptionalNumber(val);
	rawInputs.transparency = normalized;
	if (normalized === undefined) return;
	const constrained = normalizeSpinnerInput(normalized, transparencyConstraint);
	rawInputs.transparency = constrained;
	editData.transparency = constrained;
	onTransparencyChange();
}

function onTransparencyInput(event) {
	const target = event.target;
	if (!target) return;
	if (target.value === "") {
		rawInputs.transparency = undefined;
		return;
	}
	const parsed = target.valueAsNumber;
	if (!Number.isFinite(parsed)) return;
	const normalized = normalizeSpinnerInput(parsed, transparencyConstraint);
	target.valueAsNumber = normalized;
	onTransparencyChangeLocal(normalized);
}

function onColorRChange(val: unknown) {
	const normalized = toOptionalNumber(val);
	rawInputs.colorR = normalized;
	if (normalized === undefined) return;
	const constrained = normalizeSpinnerInput(normalized, colorConstraint);
	rawInputs.colorR = constrained;
	editData.color.red = constrained;
	onColorChange();
}

function onColorRInput(event) {
	const target = event.target;
	if (!target) return;
	if (target.value === "") {
		rawInputs.colorR = undefined;
		return;
	}
	const parsed = target.valueAsNumber;
	if (!Number.isFinite(parsed)) return;
	const normalized = normalizeSpinnerInput(parsed, colorConstraint);
	target.valueAsNumber = normalized;
	onColorRChange(normalized);
}

function onColorGChange(val: unknown) {
	const normalized = toOptionalNumber(val);
	rawInputs.colorG = normalized;
	if (normalized === undefined) return;
	const constrained = normalizeSpinnerInput(normalized, colorConstraint);
	rawInputs.colorG = constrained;
	editData.color.green = constrained;
	onColorChange();
}

function onColorGInput(event) {
	const target = event.target;
	if (!target) return;
	if (target.value === "") {
		rawInputs.colorG = undefined;
		return;
	}
	const parsed = target.valueAsNumber;
	if (!Number.isFinite(parsed)) return;
	const normalized = normalizeSpinnerInput(parsed, colorConstraint);
	target.valueAsNumber = normalized;
	onColorGChange(normalized);
}

function onColorBChange(val: unknown) {
	const normalized = toOptionalNumber(val);
	rawInputs.colorB = normalized;
	if (normalized === undefined) return;
	const constrained = normalizeSpinnerInput(normalized, colorConstraint);
	rawInputs.colorB = constrained;
	editData.color.blue = constrained;
	onColorChange();
}

function onColorBInput(event) {
	const target = event.target;
	if (!target) return;
	if (target.value === "") {
		rawInputs.colorB = undefined;
		return;
	}
	const parsed = target.valueAsNumber;
	if (!Number.isFinite(parsed)) return;
	const normalized = normalizeSpinnerInput(parsed, colorConstraint);
	target.valueAsNumber = normalized;
	onColorBChange(normalized);
}

/** Shadow composable's onPickerColorChange to also sync rawInputs. */
function onPickerColorChange(val: unknown) {
	baseOnPickerColorChange(val);
	rawInputs.colorR = editData.color.red;
	rawInputs.colorG = editData.color.green;
	rawInputs.colorB = editData.color.blue;
}

function onAnchorXChange(val: unknown) {
	const normalized = toOptionalNumber(val);
	rawInputs.anchorX = normalized;
	if (normalized === undefined) return;
	updateLocalAnchorPointX(normalized);
}

function onAnchorYChange(val: unknown) {
	const normalized = toOptionalNumber(val);
	rawInputs.anchorY = normalized;
	if (normalized === undefined) return;
	updateLocalAnchorPointY(normalized);
}

function onAnchorZChange(val: unknown) {
	const normalized = toOptionalNumber(val);
	rawInputs.anchorZ = normalized;
	if (normalized === undefined) return;
	updateLocalAnchorPointZ(normalized);
}

async function loadEditData() {
	await loadCommonEditData();
	initCommonRawInputs(rawInputs);
}

/**
 * Axes written together with the edited one.
 * Manhole is circular, so its hidden Y diameter always follows X.
 */
function getAxesToWrite(changedAxis: SolidMeshAxisKey): SolidMeshAxisKey[] {
	return structureType.value === SIMPLE_STRUCTURE_TYPE.MANHOLE &&
		changedAxis === "x"
		? ["x", "y"]
		: [changedAxis];
}

/**
 * Writes the edited dimension back to the viewer.
 * Untouched axes keep their stored scale, so the rounding applied for display
 * never reaches the persisted value.
 */
function onDimensionsChange(changedAxis: SolidMeshAxisKey) {
	const sm = getSolidMesh();
	if (!sm) return;
	if (
		structureType.value !== SIMPLE_STRUCTURE_TYPE.BOX_CULVERT &&
		structureType.value !== SIMPLE_STRUCTURE_TYPE.MANHOLE
	) {
		return;
	}
	isUpdatingDimensions.value = true;
	const config = sm.shapeConfig as { dimensions: TPoint3; scale: TPoint3 };
	const { dimensions, scale } = config;
	const cfg = typeConfig.value;

	const newScale = { ...scale };
	for (const axis of getAxesToWrite(changedAxis)) {
		// Manhole Y mirrors the X diameter; X is the only editable one.
		const source =
			axis === "y" && structureType.value === SIMPLE_STRUCTURE_TYPE.MANHOLE
				? "x"
				: axis;
		const meters = convertLength(
			editData.dimensions[source],
			cfg[source].baseUnit,
			"m",
		);
		newScale[axis] = meters / dimensions[axis];
	}
	(sm.setScale as (s: TPoint3) => void)(newScale);
}

async function syncCoordinatesFromViewer() {
	const sm = getSolidMesh();
	if (!sm?.isLoaded || !props.isEditing) return;
	await fetchLocalAnchorPoint();
	editData.localAnchorPoint = {
		x: localAnchorPoint.x,
		y: localAnchorPoint.y,
		z: localAnchorPoint.z,
	};
}

// ---------------------------------------------------------------------------
// Watchers
// ---------------------------------------------------------------------------

// solidMesh.isLoaded
watch(
	() =>
		(solidMesh.value as Record<string, unknown> | undefined)?.isLoaded as
			| boolean
			| undefined,
	async (isLoaded) => {
		if (isLoaded) {
			await fetchLocalAnchorPoint();
			if (props.isEditing) {
				await loadEditData();
			}
		}
	},
	{ immediate: true },
);

// isEditing
watch(
	() => props.isEditing,
	async (newVal) => {
		if (newVal) {
			await loadEditData();
		}
	},
	{ immediate: true },
);

// wgs84Center
watch(
	() => {
		const sm = solidMesh.value as Record<string, unknown> | undefined;
		const config = sm?.shapeConfig as { wgs84Center?: TPoint3 } | undefined;
		return config?.wgs84Center;
	},
	() => {
		if (props.isEditing && !isUpdatingPosition.value) {
			if (anchorFetchTimeout.value) clearTimeout(anchorFetchTimeout.value);
			anchorFetchTimeout.value = setTimeout(() => {
				syncCoordinatesFromViewer();
			}, 0);
		}
	},
	{ deep: true },
);

// scale
watch(
	() => {
		const sm = solidMesh.value as Record<string, unknown> | undefined;
		const config = sm?.shapeConfig as { scale?: TPoint3 } | undefined;
		return config?.scale;
	},
	() => {
		if (
			props.isEditing &&
			!isUpdatingDimensions.value &&
			!isDimensionInputting.value &&
			!isDimensionFieldFocused.value
		) {
			syncDimensionsFromViewer();
			// Both editData and rawInputs hold axis base-unit values.
			// DistanceSettingInput owns the conversion to the site display unit.
			rawInputs.dimensionX = editData.dimensions.x;
			rawInputs.dimensionY = editData.dimensions.y;
			rawInputs.dimensionZ = editData.dimensions.z;
		}
	},
	{ deep: true },
);

onBeforeUnmount(() => {
	if (dimensionInputTypingTimeout.value) {
		clearTimeout(dimensionInputTypingTimeout.value);
		dimensionInputTypingTimeout.value = null;
	}
});

// Sync anchorZ from displayLocalZ (handles drag preDragLocalZ).
// displayLocalZ is in metres, so it needs the same conversion as the other axes.
watch(displayLocalZ, (val) => {
	rawInputs.anchorZ = val;
});

// Sync anchorX/Y when coordinates update from viewer
watch(
	() => editData.localAnchorPoint,
	(anchor) => {
		if (!props.isEditing) return;
		rawInputs.anchorX = anchor.x;
		rawInputs.anchorY = anchor.y;
	},
	{ deep: true },
);
</script>

<style scoped>
.solid-mesh-panel {
	margin-bottom: 8px;
	background-color: rgb(var(--v-theme-panel_mian));
	opacity: 1;
}

.solid-mesh-panel :deep(.v-card-text) {
	background-color: rgb(var(--v-theme-panel_main));
	max-height: calc(100vh - 200px);
	overflow-y: auto;
}

.solid-mesh-detail-view :deep(.v-card-text) {
	background-color: rgb(var(--v-theme-surface));
}

.color-preview {
	width: 20px;
	height: 20px;
	border: 1px solid #ccc;
	border-radius: 4px;
}

.cursor-pointer {
	cursor: pointer;
}

.text-caption {
	color: rgba(255, 255, 255, 0.7);
	font-weight: 500;
}

.text-body-2 {
	color: white;
}

.gap-2 {
	gap: 8px;
}

.field-stack {
	display: flex;
	flex-direction: column;
	gap: 8px;
	width: 100%;
}

.field-stack :deep(.v-input),
.field-stack :deep(.v-text-field) {
	width: 100%;
}

.v-text-field :deep(.v-field) {
	font-size: 12px;
}

.v-text-field :deep(.v-field__input) {
	padding: 8px;
	min-height: 28px;
}

.dragging-locked :deep(.v-field) {
	background-color: rgba(100, 100, 100, 0);
}
</style>
